import { createPortal } from 'react-dom';
import type { Edge } from '@xyflow/react';
import type { WorkflowDefinitionResponse } from '@erp/rpc';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { definitionToGraph } from 'src/lib/workflows/definitionToGraph';
import { useGetWorkflowDefinition } from 'src/api/rpc/hooks/workflows/useWorkflowDefinitions';
import { WorkflowGraphView } from './WorkflowGraphView';

/** Margin kept between the preview and the viewport edge. */
const VIEWPORT_MARGIN = 12;

/** Preview size in CSS pixels — fixed, so canvas zoom doesn't shrink it. */
const PREVIEW_WIDTH = 420;
const PREVIEW_HEIGHT = 260;
/** Gap between the bottom of the host node and the top of the preview. */
const PREVIEW_OFFSET = 8;

export interface SubActionHoverPreviewAnchor {
  /** Viewport rect of the host node, from `getBoundingClientRect()`. */
  left: number;
  bottom: number;
  width: number;
}

interface SubActionHoverPreviewProps {
  /** Definition id of the sub-action to preview. */
  workflowDefinitionId: string;
  anchor: SubActionHoverPreviewAnchor;
  /** Name of the action, shown as the preview's caption. */
  label: string;
}

/** Hold `size` px of preview inside `viewport`, never crossing the near edge. */
function clampToViewport(
  position: number,
  size: number,
  viewport: number,
): number {
  const farEdge = Math.max(viewport - size - VIEWPORT_MARGIN, VIEWPORT_MARGIN);
  return Math.min(Math.max(position, VIEWPORT_MARGIN), farEdge);
}

/**
 * Drop editor edge metadata. The read-only view draws `deletable` edges itself
 * and locks them so the preview cannot delete a connection.
 */
function toPreviewGraph(definition: WorkflowDefinitionResponse) {
  const { nodes, edges } = definitionToGraph(definition);
  return {
    nodes,
    edges: edges.map((edge): Edge => ({
      id: edge.id,
      source: edge.source,
      sourceHandle: edge.sourceHandle,
      target: edge.target,
      targetHandle: edge.targetHandle,
    })),
  };
}

/**
 * Floating read-only preview of a sub-action's graph, shown beneath the node
 * while the user hovers its View control.
 *
 * Portalled to the body and positioned with `fixed` off the host node's viewport
 * rect — the same approach as the wire hover hint in `step-node`. That keeps the
 * preview a constant, readable size no matter how far the canvas is zoomed out,
 * which an in-canvas element can't do.
 */
export function SubActionHoverPreview({
  workflowDefinitionId,
  anchor,
  label,
}: SubActionHoverPreviewProps) {
  const { data: definition, isLoading } =
    useGetWorkflowDefinition(workflowDefinitionId);

  const graph = definition ? toPreviewGraph(definition) : null;

  // Keep the preview on screen — it opens below the node, so a node near the
  // bottom of the viewport needs the vertical clamp as much as one near the
  // right edge needs the horizontal one.
  const left = clampToViewport(
    anchor.left + anchor.width / 2 - PREVIEW_WIDTH / 2,
    PREVIEW_WIDTH,
    window.innerWidth,
  );
  const top = clampToViewport(
    anchor.bottom + PREVIEW_OFFSET,
    PREVIEW_HEIGHT,
    window.innerHeight,
  );

  return createPortal(
    <div
      data-testid="sub-action-hover-preview"
      className="pointer-events-none fixed z-[3000] overflow-hidden rounded-lg border bg-background shadow-xl"
      style={{
        left,
        top,
        width: PREVIEW_WIDTH,
        height: PREVIEW_HEIGHT,
      }}
    >
      <div className="truncate border-b bg-accent px-2 py-1 text-[11px] font-medium">
        {label}
      </div>
      <div className="relative h-[calc(100%-1.5rem)] w-full">
        {graph && graph.nodes.length > 0 ? (
          <WorkflowGraphView
            nodes={graph.nodes}
            edges={graph.edges}
            zipTies={definition?.zipTies}
            nested
            fitViewOptions={{ padding: 0.12, maxZoom: 1 }}
            minZoom={0.05}
            maxZoom={1}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            {isLoading && <CircleNotchIcon className="h-4 w-4 animate-spin" />}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
